import { useToastContext } from '../components/ToastProvider';
import { useCallback } from 'react';

type ToastFn = {
  success: (message: string, duration?: number) => string;
  error: (message: string, duration?: number) => string;
  loading: (message: string) => string;
  info: (message: string, duration?: number) => string;
  dismiss: (id?: string) => void;
};

/**
 * Convenience hook for firing toasts from any component inside <ToastProvider>.
 *
 * Returns a **stable** object — safe to use in `useCallback` / `useEffect` deps.
 *
 * ```tsx
 * const { success, error } = useToast();
 * success('Saved!');
 * error('Something went wrong');
 * ```
 */
export function useToast(): ToastFn {
  const { addToast, removeToast, removeAll } = useToastContext();

  return useCallback(
    () => ({
      success: (message: string, duration = 4000) =>
        addToast({ message, type: 'success', duration }),
      error: (message: string, duration = 4000) =>
        addToast({ message, type: 'error', duration }),
      loading: (message: string) =>
        addToast({ message, type: 'loading', duration: Infinity }),
      info: (message: string, duration = 4000) =>
        addToast({ message, type: 'info', duration }),
      dismiss: (id?: string) => (id ? removeToast(id) : removeAll()),
    }),
    [addToast, removeToast, removeAll],
  )();
}
